import { Content, Flex, usePlatformInfo } from '@acsbe/ui';
import { styled } from 'styled-components';
import { CustomSizeContent } from './common';
import { BillingCycle } from './Plan';
import { LicenseDetails, PlanType } from './types';

interface PlanPricingDisplayProps {
  plan: PlanType;
  billingCycle: BillingCycle;
  discountedPrice?: number;
  originalPrice?: number;
  subscriptionPrice?: number;
  hasActualDiscount: boolean;
  licenseDetails?: LicenseDetails;
}

export const PlanPricingDisplay: React.FC<PlanPricingDisplayProps> = ({
  plan,
  billingCycle,
  discountedPrice,
  originalPrice,
  subscriptionPrice,
  hasActualDiscount,
  licenseDetails,
}) => {
  const isEnterprise = plan.name?.toLowerCase() === 'enterprise';
  const { uptoTablet } = usePlatformInfo();
  const billingPeriodText = billingCycle === 'annually' ? 'year' : 'month';

  const getDisplayPrice = () => {
    if (isEnterprise) {
      return 'Custom';
    }

    if (hasActualDiscount && discountedPrice) {
      return discountedPrice;
    }

    return subscriptionPrice ?? 0;
  };

  const renderDiscountInfo = () => {
    let originalPriceValue: number | undefined;
    let savingsAmount: number | undefined;

    if (hasActualDiscount && originalPrice && discountedPrice) {
      originalPriceValue = originalPrice;
      savingsAmount = originalPrice - discountedPrice;
    } else if (
      !isEnterprise &&
      !licenseDetails?.customPrice &&
      !hasActualDiscount &&
      plan.monthlyPrice &&
      plan.price
    ) {
      originalPriceValue = plan.monthlyPrice * 12;
      savingsAmount = plan.monthlyPrice * 12 - plan.price;
    }

    if (!originalPriceValue || !savingsAmount || savingsAmount <= 0) {
      return <Flex height="24px"></Flex>;
    }

    return (
      <VisibleFlex
        cycle={billingCycle ?? ''}
        hasDiscount={hasActualDiscount}
        minWidth="142px"
        row
        align="flex-end"
        gap={1}
      >
        <Content scale="accent" emphasis="none" lineHeight={1.5}>
          <s>${originalPriceValue.toLocaleString()}</s>{' '}
          <ColorSpan hasColor>save ${savingsAmount.toLocaleString()}</ColorSpan>
        </Content>
      </VisibleFlex>
    );
  };

  const displayPrice = getDisplayPrice();

  return (
    <Flex gap={1}>
      <Flex row align="flex-end" gap={0.5}>
        <CustomSizeContent
          hasColour
          emphasis="low"
          lineHeight={1.25}
          fontSize={uptoTablet ? 28 : 32}
        >
          {typeof displayPrice === 'number'
            ? `$${displayPrice.toLocaleString()}`
            : displayPrice}
        </CustomSizeContent>
        {!isEnterprise && billingPeriodText && (
          <Flex align="flex-end" paddingBlockEnd={1}>
            <Content
              scale="alternative"
              size="sm"
              emphasis="low"
              lineHeight={1.43}
            >
              {`/${billingPeriodText}`}
            </Content>
          </Flex>
        )}
      </Flex>

      {renderDiscountInfo()}
    </Flex>
  );
};

const ColorSpan = styled.span<{ hasColor?: boolean }>`
  color: ${(props) => (props.hasColor ? '#136EF8' : '')};
  text-decoration: 0;
`;

const VisibleFlex = styled(Flex)<{ cycle: string; hasDiscount: boolean }>`
  visibility: ${(props) =>
    props.hasDiscount
      ? 'visible'
      : props.cycle !== 'annually'
        ? 'hidden'
        : 'visible'};
`;
